import SquaresAvailableAtLine from './SquaresAvailableAtLine';

interface ReservePanelProps {
  id: string;
  contestName: string;
  prizePool: string;
  /** ISO timestamp for when the Squares grid will open for selections, or null. */
  questionsPostedAt: string | null;
  /** Whether the current user already has a reservation for this contest. */
  hasReservation?: boolean;
  isSubmitting?: boolean;
  onReserve?: () => void;
}

/**
 * "Reserve a Seat" panel (DKPLAT-529) — shown on PlayView in place of the squares
 * grid when a contest hasn't opened for selections yet. Mirrors Pick'em's
 * ReservePanel (DKPLAT-528): contest name, prize pool, an "available <date>" line,
 * and the reserve CTA. Once reserved, the CTA is replaced with a confirmation
 * state — there is no un-reserve flow (V1).
 */
export default function ReservePanel({
  id,
  contestName,
  prizePool,
  questionsPostedAt,
  hasReservation = false,
  isSubmitting = false,
  onReserve,
}: ReservePanelProps) {
  return (
    <div
      data-test-id={`reserve-panel-${id}`}
      className="rounded-xl p-6 mx-4 mt-4 bg-[#242424] border border-[#373737] text-center space-y-4"
    >
      <h2 data-test-id={`reserve-panel-title-${id}`} className="font-semibold text-lg text-white">
        {contestName}
      </h2>

      <p className="text-sm text-zinc-400">
        Prize Pool:{' '}
        <span data-test-id={`reserve-panel-prize-${id}`} className="font-bold text-[#53d337]">
          {prizePool}
        </span>
      </p>

      <SquaresAvailableAtLine
        questionsPostedAt={questionsPostedAt}
        testId={`reserve-panel-questions-posted-${id}`}
      />

      {hasReservation ? (
        <div
          data-test-id={`reserve-panel-reserved-${id}`}
          className="w-full rounded-lg py-3 font-bold text-[#53d337] border-2 border-[#53d337]"
        >
          You're In
        </div>
      ) : (
        <button
          data-test-id={`reserve-panel-enter-${id}`}
          disabled={isSubmitting}
          onClick={onReserve}
          className="w-full rounded-lg flex items-center justify-center transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
          style={{
            backgroundColor: '#53D337',
            fontFamily: '"Saira Extra Condensed", sans-serif',
            fontWeight: 800,
            fontSize: '21.85px',
            lineHeight: '21.85px',
            letterSpacing: '0.5%',
            color: '#000000',
            padding: '12px 24px',
          }}
        >
          Reserve Seat | Free
        </button>
      )}
    </div>
  );
}
